import { useEffect } from 'react';
import { useQuery, skipToken } from '@apollo/client/react';
import Box from '@mui/material/Box';

import {
    IPortfolioItemChildFragment,
    ISkillDetailFragment,
    PortfolioItemChildrenDocument,
} from 'graphql/types';
import { PortfolioItemContentBase } from 'user/components/PortfolioItemDrawer/PortfolioItemContentBase';
import { ReceivedCertificatePortfolioItemTable } from 'skill/components/ReceivedCertificatePortfolioItemTable';
import { TSkillOption } from 'skill/types';
import { Loader } from 'common/components/Loader';
import { useApolloError } from 'common/hooks/useApolloError';

interface IProps extends React.ComponentProps<typeof PortfolioItemContentBase> {
    hideUserBar?: boolean;
    onUpdate(): void;
}

export const SkillPortfolioItemContent = ({
    hideUserBar,
    loading,
    portfolioItem,
    onUpdate,
    ...other
}: IProps) => {
    const { id: portfolioItemId, user, relatedObject } = portfolioItem || {};
    const { showApolloError } = useApolloError();

    const skill = relatedObject as ISkillDetailFragment;
    const skillOption: TSkillOption | undefined = skill?.id
        ? {
              id: skill.id,
              name: skill.name,
          }
        : undefined;

    const {
        data,
        loading: childrenLoading,
        error,
    } = useQuery(
        PortfolioItemChildrenDocument,
        portfolioItemId ? { variables: { id: portfolioItemId } } : skipToken
    );

    const isLoading = loading || childrenLoading;

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);

    const children =
        (data?.portfolioItem?.children as IPortfolioItemChildFragment[]) || [];
    const hasMandatoryCertificates = children.some(
        (child) => !!child.metadata?.isMandatory
    );

    if (!user) return null;

    return (
        <PortfolioItemContentBase
            {...other}
            hasMandatoryCertificates={hasMandatoryCertificates}
            loading={isLoading}
            portfolioItem={portfolioItem}
            relatedObjectDescription={skill?.description || undefined}
        >
            <Box sx={{ pb: 6, pt: 2, px: 4 }}>
                {isLoading && <Loader />}

                {!isLoading && (
                    <ReceivedCertificatePortfolioItemTable
                        certificatePortfolioItems={children}
                        hideUserBar={hideUserBar}
                        portfolioItemId={portfolioItemId}
                        skill={skillOption}
                        user={user}
                        onUpdate={onUpdate}
                    />
                )}
            </Box>
        </PortfolioItemContentBase>
    );
};
